import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSAPCDC } from '../hooks/useSAPCDC';
import { useUserVerification } from '../../sso/hooks/useUserVerification';
import { useAuth } from '../../../contexts/AuthContext';
import devInstanceAccessService from '../../../services/DevInstanceAccessService';
import { Button } from "../../../components/ui/button";
import { HelpCircle } from "lucide-react";
import AdaptLogo from "../../../images/Adapt_Logo.jpg";
import LocalhostSetupHelper from "./LocalhostSetupHelper";

/**
 * SAP CDC Login Component
 * Dedicated component for SAP CDC authentication flow
 */
const SAPCDCLogin = () => {
  const navigate = useNavigate();
  const { verifyUserAccess } = useUserVerification();
  const { login: authLogin } = useAuth();
  const { 
    login, 
    isAuthenticated, 
    user, 
    isLoading,
    error,
    token,
    showLocalhostHelper: displayLocalhostHelper 
  } = useSAPCDC();
  
  const [loginStatus, setLoginStatus] = useState('ready');
  const [showLocalhostHelper, setShowLocalhostHelper] = useState(false);
  
  // Check if running on localhost
  const isLocalHost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';

  const handleSAPCDCLogin = async () => {
    try {
      setLoginStatus('logging-in');
      console.log('\n🚀 [SAP CDC Login] Starting SAP CDC authentication...');
      
      // Show localhost helper if needed
      if (isLocalHost) {
        displayLocalhostHelper();
        setShowLocalhostHelper(true);
        return; // Don't proceed with login until user has set up cookies
      }
      
      // Perform SAP CDC login
      const loginResult = await login();
      
      if (loginResult.success && loginResult.user) {
        setLoginStatus('verifying');
        console.log('✅ [SAP CDC Login] Authentication successful');
        console.log('👤 [SAP CDC Login] User details:', {
          UID: loginResult.user.UID,
          email: loginResult.user.profile?.email,
          firstName: loginResult.user.profile?.firstName,
          lastName: loginResult.user.profile?.lastName,
          loginProvider: loginResult.user.loginProvider
        });
        
        // Extract user information
        const userEmail = loginResult.user.profile?.email;
        const userName = `${loginResult.user.profile?.firstName || ''} ${loginResult.user.profile?.lastName || ''}`.trim() ||
                        loginResult.user.profile?.email?.split('@')[0] ||
                        'SAP CDC User';
        
        if (userEmail) {
          console.log('🔍 [SAP CDC Login] Starting MongoDB verification...');
          
          // Verify user access against MongoDB instances
          const verificationResult = await verifyUserAccess(userEmail, loginResult.token?.sessionToken);
          
          if (verificationResult && verificationResult.isValid) {
            setLoginStatus('success');
            console.log('✅ [SAP CDC Login] Verification successful');
            
            // Set authentication state in context
            authLogin({
              user: userName,
              email: userEmail,
              loginType: 'SAP CDC',
              accessibleInstances: verificationResult.accessibleInstances,
              verificationResult: verificationResult
            });
            
            // Navigate to hub page (beta) or instances page, based on allowlist
            navigate(await devInstanceAccessService.getPostLoginRoute(userEmail));
          } else {
            setLoginStatus('no-access');
            console.log('❌ [SAP CDC Login] No access to any instances');
          }
        } else {
          setLoginStatus('no-email');
          console.error('❌ [SAP CDC Login] No email in user profile');
        }
      } else {
        setLoginStatus('failed');
        console.error('❌ [SAP CDC Login] Authentication failed');
      }
    } catch (error) {
      setLoginStatus('error');
      console.error('❌ [SAP CDC Login] Error:', error);
    }
  };

  // Auto-redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('ℹ️ [SAP CDC Login] User already authenticated, auto-redirecting...');
      handleSAPCDCLogin();
    }
  }, [isAuthenticated, user]);

  const getStatusMessage = () => {
    switch (loginStatus) {
      case 'logging-in':
        return 'Authenticating with SAP CDC...';
      case 'verifying':
        return 'Verifying access to Adapt instances...';
      case 'success':
        return 'Authentication successful! Redirecting...';
      case 'failed':
        return 'SAP CDC authentication failed. Please try again.';
      case 'no-access':
        return 'Authentication successful, but no access to any Adapt instances. Contact your administrator.';
      case 'no-email':
        return 'Authentication successful, but no email found in profile. Contact your administrator.';
      case 'error':
        return `Error: ${error || 'Unknown error occurred'}`;
      default:
        return 'Ready to authenticate with SAP CDC';
    }
  };

  const getStatusColor = () => {
    switch (loginStatus) {
      case 'logging-in':
      case 'verifying':
        return 'text-blue-600';
      case 'success':
        return 'text-green-600';
      case 'failed':
      case 'no-access':
      case 'no-email':
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <section className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-4xl bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col lg:flex-row" style={{ minHeight: '600px' }}>
        
        {/* Left Panel - Image Section */}
        <div className="lg:w-1/2 bg-gradient-to-br from-orange-400 to-orange-600 relative overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 bg-orange-500 bg-opacity-10"></div>
          <div className="relative z-10 p-8 text-center text-white max-w-md">
            <div className="mb-6">
              <div className="w-32 h-32 lg:w-40 lg:h-40 mx-auto mb-6 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center overflow-hidden">
                <img 
                  src={AdaptLogo}
                  alt="Adapt Logo" 
                  className="w-full h-full object-contain rounded-2xl"
                />
              </div>
            </div>
            
            <h1 className="text-2xl lg:text-3xl font-bold leading-tight">
              SAP CDC<br />
              Authentication<br />
              <span className="block mt-2">
                Secure Access
              </span>
            </h1>
          </div>
        </div>

        {/* Right Panel - Login Interface */}
        <div className="lg:w-1/2 bg-white flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-semibold text-gray-800 mb-2">SAP CDC Login</h1>
              <div className="absolute top-4 right-4">
                <button 
                  onClick={() => navigate('/')}
                  className="text-orange-600 hover:text-orange-700 text-sm underline"
                >
                  Back to Main Login
                </button>
              </div>
            </div>

            {/* Status Message */}
            <div className={`text-center mb-6 p-3 rounded-lg bg-gray-50 ${getStatusColor()}`}>
              {getStatusMessage()}
            </div>

            {/* User Info (if authenticated) */}
            {isAuthenticated && user && (
              <div className="text-center mb-6 p-3 rounded-lg bg-green-50 text-green-800">
                <div className="text-sm font-medium">Authenticated as:</div>
                <div className="text-sm">{user.profile?.email}</div>
                <div className="text-xs">{user.profile?.firstName} {user.profile?.lastName}</div>
              </div>
            )}

            {/* Login Button */}
            <Button
              onClick={handleSAPCDCLogin}
              disabled={isLoading || loginStatus === 'logging-in' || loginStatus === 'verifying'}
              className="w-full text-white py-3 text-lg font-semibold rounded transition-colors"
              style={{ 
                height: '50px', 
                backgroundColor: '#ea580c', 
                fontSize: '18px'
              }}
            >
              {isLoading || loginStatus === 'logging-in' || loginStatus === 'verifying' 
                ? 'Please wait...' 
                : isAuthenticated 
                  ? 'Continue to Instances' 
                  : 'Login with SAP CDC'
              }
            </Button>

            {/* Development Info */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-6 text-xs text-gray-500 text-center">
                <p>Development Mode</p>
                <p>API Key: {process.env.NODE_ENV === 'development' ? '4_D0XjqWc0n16uInJitxKlPA' : '4_dsSTqVefWOfKfPvzFPIbCQ'}</p>
                <p>Environment: {process.env.NODE_ENV}</p>
              </div>
            )}

            {/* Localhost Helper */}
            {isLocalHost && (
              <div className="mt-6 text-center">
                <Button
                  onClick={() => setShowLocalhostHelper(true)}
                  variant="outline"
                  size="sm"
                  className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Need Help with Localhost Setup?
                </Button>
              </div>
            )}

            {/* Localhost Helper */}
            {window.location.hostname === 'localhost' && (
              <div className="mt-6 text-xs text-yellow-600 bg-yellow-50 p-3 rounded">
                <p className="font-medium">Localhost Development:</p>
                <p>For SAP CDC to work on localhost, you may need to visit https://connect-dev.laerdal.com first to establish the necessary cookies.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Localhost Setup Helper Modal */}
      {showLocalhostHelper && (
        <LocalhostSetupHelper onClose={() => setShowLocalhostHelper(false)} />
      )}
    </section>
  );
};

export default SAPCDCLogin;